import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { isVersionConflict } from "../lib/api";
import { formatDateTime } from "../lib/missions";
import { buildModuleRequest, formatMinutes, moduleForm, moduleFormProblem, moveEntry, newObjective, trainingError } from "../lib/training";
import useJamoraCatalog from "../hooks/useJamoraCatalog";
import FeaturePicker from "../components/training/FeaturePicker";
import { FieldLabel } from "../components/training/ProgramBits";
import { StatePill } from "../components/CollaboratorBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, ExternalLink, Loader2, Plus, Target, Trash2 } from "lucide-react";

const HISTORY_LABELS = { created: "Module créé", updated: "Module modifié", retired: "Module désactivé", restored: "Module réactivé" };

// One training module: created and edited with training:manage_catalog (`can_manage` from the server), read by the other readers.
export default function TrainingModuleForm() {
  const { id } = useParams();
  const creating = !id;
  const navigate = useNavigate();
  const catalog = useJamoraCatalog(true);
  const [phase, setPhase] = useState(creating ? "ready" : "loading"); // loading | ready | missing | error
  const [errorText, setErrorText] = useState("");
  const [module, setModule] = useState(null);
  const [form, setForm] = useState(() => moduleForm(null));
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    if (creating) return;
    try {
      const { data } = await api.get(`/training/modules/${id}`);
      setModule(data);
      setForm(moduleForm(data));
      setPhase("ready");
    } catch (e) {
      if (e?.response?.status === 404) setPhase("missing");
      else { setErrorText(trainingError(e, "Impossible de charger le module.")); setPhase("error"); }
    }
  }, [creating, id]);
  useEffect(() => { load(); }, [load]);

  const canEdit = creating || module?.can_manage;
  const problem = useMemo(() => moduleFormProblem(form), [form]);
  const request = useMemo(() => buildModuleRequest(form, module), [form, module]);
  const dirty = creating || Object.keys(request).some((k) => k !== "expected_version");
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setObjective = (key, text) => setForm((f) => ({ ...f, objectives: f.objectives.map((o) => (o.key === key ? { ...o, text } : o)) }));

  const submit = async () => {
    setBusy(true);
    try {
      if (creating) {
        const { data } = await api.post("/training/modules", request);
        toast.success("Module créé");
        navigate(`/formations/modules/${data.id}`, { replace: true });
      } else {
        const { data } = await api.patch(`/training/modules/${id}`, request);
        setModule(data);
        setForm(moduleForm(data));
        toast.success("Module enregistré");
      }
    } catch (e) {
      toast.error(trainingError(e, "Impossible d'enregistrer le module."));
      if (isVersionConflict(e)) await load();
    } finally {
      setBusy(false);
    }
  };

  // After « Créer le module » the same page is reused for the new id: show the skeleton until that module is loaded.
  if (phase === "loading" || (!creating && module?.id !== id && phase === "ready")) return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-96 w-full" /></div>;
  if (phase === "missing" || phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid="training-module-missing">
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{phase === "missing" ? "Ce module n'existe pas ou vous n'y avez pas accès." : errorText}</p>
        <Button variant="outline" asChild><Link to="/formations?tab=modules">Retour aux modules</Link></Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="training-module-page">
      <Link to="/formations?tab=modules" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Retour aux modules</Link>
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="training-module-title">
          {creating ? "Nouveau module" : module.title}
        </h1>
        {!creating && <StatePill active={module.active} activeLabel="Actif" inactiveLabel="Désactivé" />}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] items-start max-w-6xl">
        {canEdit ? (
          <ModuleEditor form={form} setForm={setForm} set={set} setObjective={setObjective} catalog={catalog} problem={problem}
            dirty={dirty} busy={busy} creating={creating} onSubmit={submit} onReset={() => setForm(moduleForm(module))} />
        ) : (
          <ModuleReadView module={module} />
        )}
        <aside className="space-y-4">
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h2 className="text-sm font-semibold text-slate-900">Bien écrire un objectif</h2>
            <p>Un objectif décrit ce que le participant saura <strong>faire</strong>, de façon observable : « Encaisser une vente comptant », plutôt que « Connaître la caisse ».</p>
            <p>En séance, le formateur notera chaque objectif : acquis, partiellement acquis ou non acquis.</p>
          </Card>
          {!creating && module.programs?.length > 0 && (
            <Card className="p-4 space-y-2" data-testid="training-module-programs">
              <h2 className="text-sm font-semibold text-slate-900">Utilisé par</h2>
              <ul className="space-y-1">
                {module.programs.map((p) => (
                  <li key={p.id} className="text-sm">
                    <Link to={`/formations/programmes/${p.id}`} className="text-jamora hover:underline">{p.label}</Link>
                    <span className="text-xs text-slate-500"> · {[p.published && "version publiée", p.draft && "brouillon"].filter(Boolean).join(", ")}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500">Vos modifications apparaissent dans les brouillons ; une version publiée garde le module tel qu'il était.</p>
            </Card>
          )}
          {!creating && module.can_manage && (
            <Card className="p-4 space-y-2">
              <h2 className="text-sm font-semibold text-slate-900">{module.active ? "Désactiver le module" : "Réactiver le module"}</h2>
              <p className="text-xs text-slate-500">
                {module.active ? "Il ne pourra plus être ajouté à un programme. Les programmes publiés gardent leur copie." : "Il pourra de nouveau être ajouté aux programmes."}
              </p>
              <Button variant="outline" size="sm" onClick={() => setToggling(true)} data-testid="training-module-toggle">
                {module.active ? "Désactiver" : "Réactiver"}
              </Button>
            </Card>
          )}
          {!creating && module.history?.length > 0 && (
            <details className="rounded-lg border border-slate-200 bg-white p-4" data-testid="training-module-history">
              <summary className="cursor-pointer text-sm font-medium text-slate-900">Historique</summary>
              <ol className="mt-3 space-y-2">
                {module.history.map((h, i) => (
                  <li key={i} className="text-sm">
                    <span className="text-slate-800">{HISTORY_LABELS[h.kind] || h.kind}</span>
                    <span className="text-xs text-slate-500"> · {formatDateTime(h.at)} · {h.actor_name}</span>
                  </li>
                ))}
              </ol>
            </details>
          )}
        </aside>
      </div>

      {!creating && (
        <ToggleDialog open={toggling} module={module} onClose={() => setToggling(false)}
          onDone={(data) => { setToggling(false); setModule(data); setForm(moduleForm(data)); }} />
      )}
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <Card className="p-4 sm:p-5 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      {children}
    </Card>
  );
}

function ModuleEditor({ form, setForm, set, setObjective, catalog, problem, dirty, busy, creating, onSubmit, onReset }) {
  return (
    <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!problem && dirty) onSubmit(); }} data-testid="training-module-form">
      <Section title="Le module">
        <div className="space-y-1.5">
          <FieldLabel htmlFor="tm-title">Titre *</FieldLabel>
          <Input id="tm-title" maxLength={160} className="text-base md:text-sm" placeholder="Ex. Vente comptant au comptoir" value={form.title}
            onChange={set("title")} data-testid="training-module-title-input" />
        </div>
      </Section>

      <Section title="Objectifs" hint="Ce que le participant saura faire à la fin du module.">
        {form.objectives.length === 0 && <p className="text-sm text-slate-500">Aucun objectif pour le moment.</p>}
        <ol className="space-y-2">
          {form.objectives.map((o, index) => (
            <li key={o.key} className="flex items-center gap-1.5" data-testid={`training-objective-${index}`}>
              <Target size={14} className="shrink-0 text-slate-400" />
              <Input maxLength={300} className="text-base md:text-sm" value={o.text} placeholder="Ex. Encaisser une vente comptant" aria-label={`Objectif ${index + 1}`}
                onChange={(e) => setObjective(o.key, e.target.value)} data-testid={`training-objective-input-${index}`} />
              <Button type="button" variant="ghost" size="icon" aria-label="Monter l'objectif" disabled={index === 0}
                onClick={() => setForm((f) => ({ ...f, objectives: moveEntry(f.objectives, index, -1) }))}><ArrowUp size={14} /></Button>
              <Button type="button" variant="ghost" size="icon" aria-label="Descendre l'objectif" disabled={index === form.objectives.length - 1}
                onClick={() => setForm((f) => ({ ...f, objectives: moveEntry(f.objectives, index, 1) }))}><ArrowDown size={14} /></Button>
              <Button type="button" variant="ghost" size="icon" aria-label="Retirer l'objectif" className="text-slate-500 hover:text-red-700"
                onClick={() => setForm((f) => ({ ...f, objectives: f.objectives.filter((x) => x.key !== o.key) }))}><Trash2 size={14} /></Button>
            </li>
          ))}
        </ol>
        <Button type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, objectives: [...f.objectives, newObjective()] }))}
          disabled={form.objectives.length >= 30} data-testid="training-objective-add"><Plus size={14} className="mr-1.5" />Ajouter un objectif</Button>
      </Section>

      <Section title="Fonctionnalités Jamora" hint="Les écrans du logiciel que ce module fait pratiquer.">
        {catalog.status === "error" ? <p className="text-sm text-amber-800">Le catalogue des fonctionnalités n'a pas pu être chargé.</p> : (
          <FeaturePicker catalog={catalog} value={form.feature_codes} onChange={(codes) => setForm((f) => ({ ...f, feature_codes: codes }))}
            disabled={catalog.status !== "ready"} testid="training-module-features" />
        )}
      </Section>

      <Section title="Déroulé">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <FieldLabel htmlFor="tm-duration" hint="Indicative, en minutes.">Durée</FieldLabel>
            <Input id="tm-duration" type="number" inputMode="numeric" min={5} max={2400} className="text-base md:text-sm" value={form.duration_minutes}
              onChange={set("duration_minutes")} data-testid="training-module-duration" />
          </div>
          <div className="space-y-1.5">
            <FieldLabel htmlFor="tm-threshold" hint="Part des objectifs à acquérir, en %.">Seuil de réussite</FieldLabel>
            <Input id="tm-threshold" type="number" inputMode="numeric" min={1} max={100} className="text-base md:text-sm" value={form.pass_threshold}
              onChange={set("pass_threshold")} data-testid="training-module-threshold" />
          </div>
        </div>
        <div className="space-y-1.5">
          <FieldLabel htmlFor="tm-prerequisites">Prérequis</FieldLabel>
          <Textarea id="tm-prerequisites" rows={2} maxLength={4000} className="text-base md:text-sm" value={form.prerequisites} onChange={set("prerequisites")} />
        </div>
        <div className="space-y-1.5">
          <FieldLabel htmlFor="tm-materials">Matériel</FieldLabel>
          <Textarea id="tm-materials" rows={2} maxLength={4000} className="text-base md:text-sm" placeholder="Ex. caisse de test, imprimante de tickets"
            value={form.materials} onChange={set("materials")} />
        </div>
        <div className="space-y-1.5">
          <FieldLabel htmlFor="tm-support" hint="Lien vers les diapositives, la vidéo ou le document du formateur.">Support</FieldLabel>
          <Input id="tm-support" type="url" maxLength={500} className="text-base md:text-sm" placeholder="https://" value={form.support_url}
            onChange={set("support_url")} data-testid="training-module-support" />
        </div>
        <div className="space-y-1.5">
          <FieldLabel htmlFor="tm-exercises">Exercices</FieldLabel>
          <Textarea id="tm-exercises" rows={3} maxLength={4000} className="text-base md:text-sm" value={form.exercises} onChange={set("exercises")} />
        </div>
      </Section>

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
        {problem && dirty && <p className="text-sm text-amber-800 sm:mr-auto" role="alert" data-testid="training-module-problem">{problem}</p>}
        {!creating && dirty && <Button type="button" variant="ghost" onClick={onReset} disabled={busy}>Annuler les modifications</Button>}
        {creating && <Button type="button" variant="outline" asChild><Link to="/formations?tab=modules">Annuler</Link></Button>}
        <Button type="submit" disabled={busy || !!problem || !dirty} data-testid="training-module-save">
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{creating ? "Créer le module" : "Enregistrer"}
        </Button>
      </div>
    </form>
  );
}

function ReadRow({ label, children }) {
  if (!children) return null;
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800 whitespace-pre-line break-words">{children}</dd>
    </div>
  );
}

function ModuleReadView({ module }) {
  return (
    <Card className="p-4 sm:p-5 space-y-4" data-testid="training-module-read">
      <div className="space-y-2">
        <h2 className="text-base font-semibold text-slate-900">Objectifs</h2>
        {module.objectives.length ? (
          <ul className="space-y-1">{module.objectives.map((o) => <li key={o.id} className="flex items-start gap-1.5 text-sm text-slate-700"><Target size={13} className="mt-0.5 text-slate-400" />{o.text}</li>)}</ul>
        ) : <p className="text-sm text-slate-500">Aucun objectif.</p>}
      </div>
      {module.features.length > 0 && (
        <div className="flex flex-wrap gap-1.5">{module.features.map((f) => <span key={f.code} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{f.label}</span>)}</div>
      )}
      <dl className="grid gap-3 sm:grid-cols-2">
        <ReadRow label="Durée">{formatMinutes(module.duration_minutes)}</ReadRow>
        <ReadRow label="Seuil de réussite">{module.pass_threshold ? `${module.pass_threshold} %` : null}</ReadRow>
      </dl>
      <dl className="space-y-3">
        <ReadRow label="Prérequis">{module.prerequisites}</ReadRow>
        <ReadRow label="Matériel">{module.materials}</ReadRow>
        <ReadRow label="Exercices">{module.exercises}</ReadRow>
        <ReadRow label="Support">{module.support_url && (
          <a href={module.support_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-jamora hover:underline break-all">
            {module.support_url}<ExternalLink size={12} />
          </a>
        )}</ReadRow>
      </dl>
    </Card>
  );
}

function ToggleDialog({ open, module, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/training/modules/${module.id}`, { expected_version: module.version, active: !module.active });
      toast.success(module.active ? "Module désactivé" : "Module réactivé");
      onDone(data);
    } catch (e) {
      toast.error(trainingError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="training-module-toggle-dialog">
        <DialogHeader>
          <DialogTitle>{module.active ? `Désactiver « ${module.title} » ?` : `Réactiver « ${module.title} » ?`}</DialogTitle>
          <DialogDescription>
            {module.active ? "Il ne pourra plus être ajouté à un programme. Un brouillon qui le contient devra le retirer avant d'être publié ; les versions publiées ne changent pas."
              : "Il pourra de nouveau être ajouté aux programmes."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="training-module-toggle-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{module.active ? "Désactiver" : "Réactiver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
